/**
 * useLogout Hook
 *
 * Hook for handling logout with proper cleanup
 */

import { useCallback } from 'react';
import { signOut } from 'next-auth/react';
import { clearAuthData } from '../stores/auth.store';

/**
 * useLogout hook return type
 */
export interface UseLogoutReturn {
    logout: () => Promise<void>;
    logoutWithRedirect: (callbackUrl?: string) => Promise<void>;
}

/**
 * useLogout hook
 *
 * Provides logout functionality with Legend-State cleanup
 *
 * @example
 * ```tsx
 * const { logout, logoutWithRedirect } = useLogout();
 *
 * // Simple logout
 * <button onClick={logout}>Đăng xuất</button>
 *
 * // Logout and redirect to home
 * <button onClick={() => logoutWithRedirect('/')}>Đăng xuất</button>
 * ```
 */
export const useLogout = (): UseLogoutReturn => {
    // Simple logout without redirect
    const logout = useCallback(async () => {
        clearAuthData();
        await signOut({ redirect: false });
    }, []);

    // Logout with redirect
    const logoutWithRedirect = useCallback(async (callbackUrl: string = '/') => {
        clearAuthData();
        await signOut({ callbackUrl });
    }, []);

    return {
        logout,
        logoutWithRedirect,
    };
};
